import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { ME } from './graphql/operations';

const AuthContext = createContext(null);

const USER_CACHE_KEY = 'sa_user_cache';
const PRODUCTS_CACHE_KEY = 'sa_products_cache';

function getCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setCachedUser(userData) {
  try {
    if (userData) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
    } else {
      localStorage.removeItem(USER_CACHE_KEY);
    }
  } catch {}
}

export function AuthProvider({ children }) {
  const client = useApolloClient();
  const [user, setUserState] = useState(() => {
    const token = localStorage.getItem('sa_token');
    return token ? getCachedUser() : null;
  });
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((nextUser) => {
    setUserState((prev) => {
      const resolved = typeof nextUser === 'function' ? nextUser(prev) : nextUser;
      setCachedUser(resolved);
      return resolved;
    });
  }, []);

  const syncProductsCache = useCallback((productsList) => {
    try {
      if (Array.isArray(productsList)) {
        localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(productsList));
      }
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('sa_token');
    if (token) {
      const cached = getCachedUser();
      if (cached) {
        setUserState(cached);
      }
      client.query({ query: ME, fetchPolicy: 'network-only' })
        .then(({ data }) => {
          if (data?.me) {
            setUser(data.me);
          } else if (cached) {
            // Fallback to cached user if cold-start response was empty
            setUserState(cached);
          }
        })
        .catch(() => {
          // Preserve cached session on transient network errors during refresh
          if (cached) setUserState(cached);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [client, setUser]);

  const login = (token, userData) => {
    localStorage.setItem('sa_token', token);
    setUser(userData);
  };

  const refreshUser = async () => {
    try {
      const { data } = await client.query({ query: ME, fetchPolicy: 'network-only' });
      if (data?.me) {
        setUser(data.me);
        return data.me;
      }
    } catch (err) {
      console.error('[AuthContext] refreshUser error:', err);
    }
    return getCachedUser();
  };

  const logout = () => {
    localStorage.removeItem('sa_token');
    localStorage.removeItem(USER_CACHE_KEY);
    localStorage.removeItem(PRODUCTS_CACHE_KEY);
    sessionStorage.removeItem('sa_active_tab');
    setUserState(null);
    client.clearStore();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, refreshUser, syncProductsCache }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
